import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ServiceReportsState } from "./models/ServiceReportsModel";
import customerMachineServiceReportsApi from "./api/customerMachineServiceReportsApi";
import customerCategoriesApi from "../shared/api/customerCategoriesApi";
import { MachinePartGroup } from "./models/ServiceReportsModel";

export const getServiceReports = createAsyncThunk(
  "customerServiceReportsSlice/getServiceReports",
  async ({ query, machineId }: any) => {
    return customerMachineServiceReportsApi.getServiceReports(query, machineId);
  }
);

export const getServiceReport = createAsyncThunk(
  "customerServiceReportsSlice/getServiceReport",
  async (serviceReportNumber: string) => {
    return customerMachineServiceReportsApi.getServiceReport(serviceReportNumber);
  }
);

export const createServiceReport = createAsyncThunk(
  "customerServiceReportsSlice/createServiceReport",
  async ({ model, machineId }: any) => {
    return customerMachineServiceReportsApi.createServiceReport(model, machineId);
  }
);

export const changeServiceReport = createAsyncThunk(
  "customerServiceReportsSlice/changeServiceReport",
  async ({ serviceReportNumber, model }: any) => {
    return customerMachineServiceReportsApi.changeServiceReport(serviceReportNumber, model);
  }
);

export const createSolvingServiceReport = createAsyncThunk(
  "customerServiceReportsSlice/createSolvingServiceReport",
  async ({ serviceReportNumber, model }: any) => {
    return customerMachineServiceReportsApi.createSolvingServiceReport(serviceReportNumber, model);
  }
);

export const changeSolvingServiceReport = createAsyncThunk(
  "customerServiceReportsSlice/changeSolvingServiceReport",
  async ({ serviceReportNumber, model }: any) => {
    return customerMachineServiceReportsApi.changeSolvingServiceReport(serviceReportNumber, model);
  }
);

export const deleteServiceReport = createAsyncThunk(
  "customerServiceReportsSlice/deleteServiceReport",
  async (serviceReportNumber: string) => {
    return customerMachineServiceReportsApi.deleteServiceReport(serviceReportNumber);
  }
);

export const getCategories = createAsyncThunk("customerServiceReportsSlice/getCategories", async () => {
  return customerCategoriesApi.getCategories();
});

export const getMachineParts = createAsyncThunk("customerServiceReportsSlice/getMachineParts", async () => {
  return customerMachineServiceReportsApi.getMachineParts();
});

const serviceReportsState: ServiceReportsState = {
  serviceReportsQuery: {
    search: undefined,
    statusCodes: [],
    openDateFrom: undefined,
    openDateTo: undefined
  },
  serviceReports: undefined,
  serviceReport: undefined,
  categories: [],
  machineParts: [],
  loading: false,
  success: undefined,
  error: undefined,
  hasAnyServiceReports: undefined,
  totalCount: 0,
  totalPages: 0
};

const customerServiceReportsSlice = createSlice({
  name: "customerServiceReportsSlice",
  initialState: serviceReportsState,
  reducers: {
    selectServiceReport: (state, action) => {
      state.serviceReport = action.payload;
    },
    changeServiceReportQuery: (state, action) => {
      state.serviceReportsQuery = action.payload;
    },
    clearServiceReports: (state) => {
      state.serviceReports = undefined;
      state.serviceReport = undefined;
      state.hasAnyServiceReports = undefined;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setHasAnyServiceReports: (state, action) => {
      state.hasAnyServiceReports = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServiceReports.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServiceReports.fulfilled, (state, { payload, meta }) => {
        state.serviceReports = payload.items;
        state.totalCount = payload.totalCount;
        state.totalPages = payload.totalPages;
        state.loading = false;
        const isNoFilter =
          (!meta.arg.query?.search || meta.arg.query?.search === "") &&
          (!meta.arg.query?.statusCodes || meta.arg.query.statusCodes.length === 0) &&
          !meta.arg.query?.openDateFrom &&
          !meta.arg.query?.openDateTo;
        if (isNoFilter) {
          state.hasAnyServiceReports = payload.items && payload.items.length > 0;
        }
      })
      .addCase(getServiceReports.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getServiceReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServiceReport.fulfilled, (state, { payload }) => {
        state.serviceReport = payload;
        state.loading = false;
      })
      .addCase(getServiceReport.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(createServiceReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(createServiceReport.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(createServiceReport.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(changeServiceReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeServiceReport.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(changeServiceReport.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(createSolvingServiceReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSolvingServiceReport.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(createSolvingServiceReport.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(changeSolvingServiceReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeSolvingServiceReport.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(changeSolvingServiceReport.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCategories.fulfilled, (state, { payload }) => {
        state.categories = payload;
        state.loading = false;
      })
      .addCase(getCategories.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getMachineParts.pending, (state) => {
        // Don't set loading = true to avoid blocking UI
      })
      .addCase(getMachineParts.fulfilled, (state, { payload }) => {
        state.machineParts = payload;
      })
      .addCase(getMachineParts.rejected, (state, { error }) => {
        state.error = error;
      });
  }
});

export const { selectServiceReport, changeServiceReportQuery, clearServiceReports, setSuccess, setLoading, setError, setHasAnyServiceReports } =
  customerServiceReportsSlice.actions;

export const selectHasAnyServiceReports = (state: any) => state.customerServiceReports.hasAnyServiceReports;

export default customerServiceReportsSlice.reducer;